// ======================================================================
// 📁 File: src/modules/ppe/pages/engineering/product-family/ProductFamilyListPage.jsx
// 📝 Purpose:
//   Product Family Master – List Page
//   - Gradient header (ERP standard)
//   - KPI bar (Total / Active / Inactive / Variants / Used)
//   - Search + status filter
//   - Table view
//   - Drawer-based Create/Edit (CONNECTED)
// ======================================================================

import React, { useEffect, useState, useCallback } from "react";
import ProductFamilyKpiBar from "../../../components/engineering/product-family/ProductFamilyKpiBar";
import ProductFamilyDrawer from "../../../pages/engineering/product-family/ProductFamilyDrawer";
import axiosClient from "../../../../../api/axiosClient";

export default function ProductFamilyListPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("ALL"); // ALL | ACTIVE | INACTIVE

  // ------------------------------------------------------------
  // Drawer state (NEW)
  // ------------------------------------------------------------
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState("create"); // create | edit
  const [selected, setSelected] = useState(null);

  // ------------------------------------------------------------
  // Load Product Families
  // ------------------------------------------------------------
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosClient.get(
        "/api/ppe/masters/product-families",
        {
          params: {
            q: search || undefined,
            is_active:
              status === "ALL"
                ? undefined
                : status === "ACTIVE"
                ? 1
                : 0,
          },
        }
      );

      setRows(res.data?.data || []);
    } finally {
      setLoading(false);
    }
  }, [search, status]);

  useEffect(() => {
    load();
  }, [load]);

  // ------------------------------------------------------------
  // Drawer handlers (NEW)
  // ------------------------------------------------------------
  const openCreateDrawer = () => {
    setSelected(null);
    setDrawerMode("create");
    setDrawerOpen(true);
  };

  const openEditDrawer = (record) => {
    setSelected(record);
    setDrawerMode("edit");
    setDrawerOpen(true);
  };

  // ------------------------------------------------------------
  // Render
  // ------------------------------------------------------------
  return (
    <div>
      {/* ====================================================== */}
      {/* Header */}
      {/* ====================================================== */}
      <div
        style={{
          background:
            "linear-gradient(90deg, #4f46e5 0%, #6366f1 50%, #7c3aed 100%)",
          borderRadius: 12,
          padding: "16px 20px",
          color: "#fff",
          marginBottom: 20,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <h4 style={{ margin: 0 }}>Product Family Master</h4>
          <div style={{ fontSize: 13, opacity: 0.9 }}>
            Engineering configuration
          </div>
        </div>

        <button className="btn btn-light" onClick={openCreateDrawer}>
          + Create Product Family
        </button>
      </div>

      {/* ====================================================== */}
      {/* KPI BAR */}
      {/* ====================================================== */}
      <ProductFamilyKpiBar rows={rows} />

      {/* ====================================================== */}
      {/* Filters */}
      {/* ====================================================== */}
      <div
        style={{
          display: "flex",
          gap: 12,
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <input
          className="form-control"
          placeholder="Search Product Family"
          style={{ maxWidth: 260 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <select
          className="form-select"
          style={{ maxWidth: 160 }}
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="ALL">All Status</option>
          <option value="ACTIVE">Active</option>
          <option value="INACTIVE">Inactive</option>
        </select>

        <button className="btn btn-primary" onClick={load}>
          Apply
        </button>
      </div>

      {/* ====================================================== */}
      {/* Table */}
      {/* ====================================================== */}
      <div className="card">
        <div className="card-body p-0">
          <table className="table table-bordered mb-0">
            <thead className="table-light">
              <tr>
                <th>Name</th>
                <th>Code</th>
                <th>Status</th>
                <th>Variants</th>
                <th>Used in Orders</th>
                <th style={{ width: 120 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className="text-center">
                    Loading...
                  </td>
                </tr>
              )}

              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center text-muted">
                    No product families found
                  </td>
                </tr>
              )}

              {rows.map((r) => (
                <tr key={r.id}>
                  <td>{r.name}</td>
                  <td>{r.code}</td>
                  <td>
                    <span
                      className={`badge ${
                        r.is_active ? "bg-success" : "bg-secondary"
                      }`}
                    >
                      {r.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td>{r.variant_count || 0}</td>
                  <td>{r.used_in_orders ? "Yes" : "No"}</td>
                  <td>
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => openEditDrawer(r)}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ====================================================== */}
      {/* Drawer */}
      {/* ====================================================== */}
      <ProductFamilyDrawer
        open={drawerOpen}
        mode={drawerMode}
        record={selected}
        onClose={() => setDrawerOpen(false)}
        onSaved={load}
      />
    </div>
  );
}
